import { useEffect, useState } from 'react';
import { ESSENTIAL_DISCOUNT, STANDARD_DISCOUNT } from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { essentialsAppPoints, standardAppPoints } from '../constants/default';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {boolean} breach : Includes Breach
 * @returns {Array}
 */

export interface PropsFunc {
  userAmount: number;
  envAmount: number;
  tileValueSelected: string;
  additionalUsers: boolean;
  nonProdEnv: boolean;
  onInspection: boolean;
  currency: string;
  localeCode: string;
  concurrentUsersAmt: number;
  authUsersAmt: number;
  healthScoresAmt: number;
  setTotalStdBaseAppPoints: React.Dispatch<React.SetStateAction<number>>;
  country: string;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

type typeOfPart = {
  qty: number;
  price: number;
};

const partNumber = {
  SaaS: 'D0A51ZX',
};

const partsToQuery = [
  {
    partNumber: partNumber.SaaS,
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const usePrice = ({
  currency,
  userAmount,
  envAmount,
  tileValueSelected,
  additionalUsers,
  nonProdEnv,
  onInspection,
  localeCode,
  concurrentUsersAmt,
  authUsersAmt,
  healthScoresAmt,
  setTotalStdBaseAppPoints,
  country,
}: PropsFunc) => {
  const [totalCost, setTotalCost] = useCurrency(currency, localeCode, 0);
  const [totalCostMonthly, setTotalCostMonthly] = useCurrency(
    currency,
    localeCode,
    0,
  );
  const [appPointPrice, setAppPointPrice] = useState(0);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);
  const calculatedPrice = () => {
    let price;
    if (tileValueSelected === 'essentials') {
      if (!onInspection && !nonProdEnv && !additionalUsers) {
        price =
          essentialsAppPoints.maintenanceBaseAppPoint *
          appPointPrice *
          ESSENTIAL_DISCOUNT;
      } else if (!onInspection && additionalUsers && !nonProdEnv) {
        price =
          (essentialsAppPoints.maintenanceBaseAppPoint +
            essentialsAppPoints.additionalUserAppPoint) *
          appPointPrice *
          ESSENTIAL_DISCOUNT;
      } else if (!onInspection && !additionalUsers && nonProdEnv) {
        price =
          (essentialsAppPoints.maintenanceBaseAppPoint +
            essentialsAppPoints.additionalEnvAppPoint) *
          appPointPrice *
          ESSENTIAL_DISCOUNT;
      } else if (!onInspection && additionalUsers && nonProdEnv) {
        price =
          (essentialsAppPoints.maintenanceBaseAppPoint +
            essentialsAppPoints.additionalEnvAppPoint +
            essentialsAppPoints.additionalUserAppPoint) *
          appPointPrice *
          ESSENTIAL_DISCOUNT;
      } else {
        price =
          essentialsAppPoints.inspectionBaseAppPoint *
          appPointPrice *
          ESSENTIAL_DISCOUNT;
      }
    } else {
      const totalAppPoints = getStdAppPoints();
      if (totalAppPoints <= standardAppPoints.minPoint) {
        price = standardAppPoints.minPoint * appPointPrice * STANDARD_DISCOUNT;
      } else {
        price = totalAppPoints * appPointPrice * STANDARD_DISCOUNT;
      }
    }
    return price;
  };

  const getStdAppPoints = () => {
    const totalPoints =
      standardAppPoints.default +
      standardAppPoints.concurrentUsers * concurrentUsersAmt +
      standardAppPoints.authorizedUsers * authUsersAmt +
      healthScoresAmt / standardAppPoints.healthScores;
    return totalPoints;
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const listPrice = products.find((i) => i.partNumber == partNumber.SaaS);
      let value = {} as typeOfPart | undefined;
      if (listPrice) {
        value = listPrice.scaleQtyPrice.find((i) => i.qty === 1);
        if (value) {
          setAppPointPrice(value?.price);
        }
      }
    }
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const price = calculatedPrice();
  const stdAppPoints = getStdAppPoints();

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [currency]);

  useEffect(() => {
    setTotalCost(price * 12);
    setTotalCostMonthly(price);
  }, [envAmount, userAmount, tileValueSelected, appPointPrice, onInspection]);

  useEffect(() => {
    setTotalCost(price * 12);
    setTotalCostMonthly(price);
    setTotalStdBaseAppPoints(stdAppPoints);
  }, [concurrentUsersAmt, authUsersAmt, healthScoresAmt, tileValueSelected]);

  return [totalCost, totalCostMonthly, loading, fetchingError];
};
